import React, {useState} from "react";
import { Grid, TextField, Typography, Box, MenuItem } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Mechanics } from "../models/mechanicsConfiguration.ts";
import { getHousingInfo } from "../utils/housingDetails.ts";
import FieldWithTrailingIcon from "./FieldWithTrailingIcon.tsx"
import InfoDialog from "./InfoDialog.tsx"; 
import { ImageBox } from "./ImageBox.tsx";
interface Props {
  data: Mechanics;
  showDutSize: boolean;
  onChange: (field: string, value: any) => void;
}

const MechanicsForm: React.FC<Props> = ({ data, showDutSize, onChange }) => {
  const [userInterfaceInfoOpen, setUserInterfaceInfoOpen] = useState(false);
  const [automaticStartInfoOpen, setAutomaticStartInfoOpen] = useState(false);

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;
      if (field === "housing") {
        onChange(field, value.toLowerCase());
      }
      else if (field === "switch" || field === "user_interface") {
        onChange(field, value === "Ja" ? true : false);
      }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Gehäuse
      </Typography>
      <Box mt={2} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            select
            label="Gehäuse Typ"
            value={data.housing.toUpperCase()}
            onChange={handleChange("housing")}
          >
            {["S2", "M2", "L2", "L3"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Box mt={2} />
          <FieldWithTrailingIcon
            icon={<InfoOutlinedIcon fontSize="small" />}
            onIconClick={() => setUserInterfaceInfoOpen(true)}
          >
            <TextField
              fullWidth
              select
              label="Mit Bedienelement"
              value={data.user_interface === true ? "Ja" : "Nein"}
              onChange={handleChange("user_interface")}
            >
              {["Ja", "Nein"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </FieldWithTrailingIcon>
          <Box mt={2} />
          <FieldWithTrailingIcon
            icon={<InfoOutlinedIcon fontSize="small" />}
            onIconClick={() => setAutomaticStartInfoOpen(true)}
          >
            <TextField
              fullWidth
              select
              label="Mit automatischer Schließerkennung"
              value={ data.switch === true ? "Ja" : "Nein" }
              onChange={handleChange("switch")}
            >
              {["Ja", "Nein"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </FieldWithTrailingIcon>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth 
                  label="Größe Gerät"
                  value={getHousingInfo(data)?.deviceSize ?? ""}
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Max. Größe DUT"
                  value={`${getHousingInfo(data)?.dutX} x ${getHousingInfo(data)?.dutY} mm`}
                  disabled
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Anzahl Elektronikmodule"
                  value={getHousingInfo(data)?.maxModules ?? ""}
                  disabled
                />
              </Grid>
            </Grid>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ImageBox
            width="100%"
            height={200}
            src={getHousingInfo(data)?.image}
            alt="Gehäuseabbildung"
          />
        </Grid>
      </Grid>
      {showDutSize === true && (
        <>
          <Box mt={2} />
          <Typography variant="h6" gutterBottom>
            DUT Maße
          </Typography>
          <Box mt={2} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Breite (mm)"
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={data.dut_width}
                onChange={(e) => onChange("dut_width", parseFloat(e.target.value))}
              />
              <Box mt={2} />
              <TextField
                fullWidth
                label="Höhe (mm)"
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={data.dut_height}
                onChange={(e) => onChange("dut_height", parseFloat(e.target.value))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ImageBox
                width="100%"
                height={128}
                src="/mechanics/DutSize.png"
                srcDarkMode="/mechanics/DutSize_DarkMode.png"
                alt="DUT Größe"
              />
            </Grid>
          </Grid>
        </>
      )}
      <InfoDialog
        open={userInterfaceInfoOpen}
        onClose={() => setUserInterfaceInfoOpen(false)}
        title="Bedienelement"
        text="Das Bedienelement ist ein Taster mit roter Beleuchtung und ein Taster mit grüner Beleuchtung. Mit dem Bedienelement kann das Gerät ohne angeschlossene Maus und Tastatur bedient werden, wenn z.B. Manuelle Eingaben im Prüfablauf notwendig sind oder wenn ein Prüfablauf neu gestartet werden muss. Die Integrierten LEDs zeigen den Status der Prüfung an, sodass einfache Programmier- oder Prüfaufgaben auch ohne angeschlossenene Monitor durchführbar sind."
        imageSrc="/mechanics/UserInterface.png"
      />
      <InfoDialog
        open={automaticStartInfoOpen}
        onClose={() => setAutomaticStartInfoOpen(false)}
        title="Automatische Schließerkennung"
        text="Mit dieser Option wird das Schließen des Adapters erkannt und der Prüf- bzw. Programmierprozess wird automatisch gestartet. Es ist dann für den Start kein Tastendruck des Bedienelements oder per Maus notwendig."
      />
    </>
  );
}; 

export default MechanicsForm;
